'use client';
import { type RefObject, useEffect } from 'react';

/**
 * When the user scrolls the sidebar to its bottom (or top) boundary,
 * the remaining wheel delta is forwarded to the content column on the right.
 *
 * Requires a non-passive wheel listener so we can call preventDefault
 * before the browser's native scroll kicks in.
 */
export function useSidebarScrollChain(
  sidebarRef: RefObject<HTMLDivElement | null>,
  contentRef: RefObject<HTMLDivElement | null>
) {
  useEffect(() => {
    const sidebar = sidebarRef.current;
    const content = contentRef.current;
    if (!sidebar || !content) return;

    const handleWheel = (e: WheelEvent) => {
      const isAtBottom =
        Math.round(sidebar.scrollTop + sidebar.clientHeight) >= sidebar.scrollHeight - 1;
      const isAtTop = sidebar.scrollTop <= 1;

      const isScrollingDown = e.deltaY > 0;
      const isScrollingUp = e.deltaY < 0;

      if ((isAtBottom && isScrollingDown) || (isAtTop && isScrollingUp)) {
        content.scrollBy({
          top: e.deltaY,
          behavior: 'auto',
        });
      }
    };

    sidebar.addEventListener('wheel', handleWheel, { passive: true });
    return () => sidebar.removeEventListener('wheel', handleWheel);
  }, [sidebarRef, contentRef]);
}
